
import React from 'react';
import { AlertCircle, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

interface UnifiedErrorProps {
  message: string;
  title?: string;
  onRetry?: () => void;
  retryText?: string;
  className?: string;
  variant?: 'default' | 'destructive';
  fullScreen?: boolean;
}

export const UnifiedError: React.FC<UnifiedErrorProps> = ({
  message,
  title = "Error",
  onRetry,
  retryText = "Try Again",
  className,
  variant = 'destructive',
  fullScreen = false
}) => {
  const containerClasses = fullScreen 
    ? 'flex justify-center items-center min-h-screen p-4'
    : 'p-4';

  return (
    <div className={cn(containerClasses, className)}>
      <div className="max-w-md w-full">
        <Alert variant={variant} className="relative border-2">
          <button
            aria-label="Close alert"
            onClick={() => (window?.history?.state ? window.history.back() : null)}
            className="absolute right-2 top-2 z-10 rounded-md p-1 text-foreground/60 hover:text-foreground focus:outline-none"
          >
            <X className="h-4 w-4" />
          </button>
          <AlertCircle className="h-5 w-5" />
          <AlertTitle className="text-lg font-semibold mb-2">{title}</AlertTitle>
          <AlertDescription className="mb-4 text-sm leading-relaxed">
            {message}
          </AlertDescription>
          {onRetry && (
            <Button 
              onClick={onRetry}
              variant="outline"
              size="sm"
              className="mt-2 flex items-center gap-2 hover:bg-accent"
            >
              <RefreshCw className="h-4 w-4" />
              {retryText}
            </Button>
          )}
        </Alert>
      </div>
    </div>
  );
};
